// frontend/src/pages/reports/TransferRequestsReport.js
import React, { useEffect, useState } from 'react';
import { Paper, Table, TableHead, TableRow, TableCell, TableBody, Chip, Box, Typography, TextField, InputAdornment, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import ReportTemplate from '../../components/reports/ReportTemplate';
import { getTransferRequestsReport } from '../../services/reportService';

const TransferRequestsReport = () => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [search, setSearch]   = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const load = async () => {
    setLoading(true); setError('');
    try { const res = await getTransferRequestsReport(); setData(res.data.data); }
    catch (err) { setError(err.response?.data?.message || 'Failed to load report'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const allRequests = data?.requests || [];
  const requests = allRequests.filter(r => {
    const matchSearch = r.personnelName.toLowerCase().includes(search.toLowerCase()) || r.serviceNumber.toLowerCase().includes(search.toLowerCase()) || r.requestedBy.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'ALL' || r.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const summary = data?.summary;
  const summaryItems = summary ? [
    { label: 'Total Requests',    value: summary.totalRequests,    color: 'primary.main', bg: '#e3f2fd' },
    { label: 'Pending',           value: summary.pendingRequests,  color: 'warning.main', bg: '#fff3e0' },
    { label: 'Approved',          value: summary.approvedRequests, color: 'success.main', bg: '#e8f5e9' },
    { label: 'Rejected',          value: summary.rejectedRequests, color: 'error.main',   bg: '#ffebee' },
  ] : [];

  const exportCols = [
    { key:'serviceNumber', label:'Personnel SN' }, { key:'personnelName', label:'Personnel Name' },
    { key:'rank', label:'Rank' }, { key:'verificationStatus', label:'Verification Status' },
    { key:'requestedBy', label:'Requested By (Officer)' }, { key:'requestedBySN', label:'Officer SN' },
    { key:'fromCommand', label:'From Command' }, { key:'fromCommandCode', label:'From Code' }, { key:'fromZone', label:'From Zone' },
    { key:'toCommand', label:'To Command' }, { key:'toCommandCode', label:'To Code' }, { key:'toZone', label:'To Zone' },
    { key:'reason', label:'Reason' }, { key:'status', label:'Status' },
    { key:'adminNote', label:'Admin Note' }, { key:'approvedBy', label:'Resolved By' },
    { key:'requestedAt', label:'Requested At' }, { key:'resolvedAt', label:'Resolved At' },
  ];

  return (
    <ReportTemplate title="Transfer Requests Report" subtitle={data?.exercise ? `${data.exercise.name} — ${data.exercise.year}` : ''}
      loading={loading} error={error} onRefresh={load}
      exportFileName="Transfer_Requests_Report" exportData={allRequests} exportColumns={exportCols}
      summaryItems={summaryItems}>
      <Paper elevation={2} sx={{ p: 3 }}>
        <Box display="flex" gap={2} mb={2} flexWrap="wrap">
          <TextField size="small" placeholder="Search by personnel or officer..." value={search}
            onChange={(e) => setSearch(e.target.value)} sx={{ flex:1, minWidth:220 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
              {['ALL','PENDING','APPROVED','REJECTED'].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor:'grey.100' }}>
              {['Personnel SN','Name','Rank','Requested By','From','To','Reason','Status','Admin Note','Date'].map(h =>
                <TableCell key={h}><strong>{h}</strong></TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell sx={{fontWeight:'bold'}}>{r.serviceNumber}</TableCell>
                <TableCell>
                  <Typography variant="body2">{r.personnelName}</Typography>
                  <Typography variant="caption" color="text.secondary">{r.rank}</Typography>
                </TableCell>
                <TableCell>{r.rank}</TableCell>
                <TableCell>
                  <Typography variant="body2">{r.requestedBy}</Typography>
                  <Typography variant="caption" color="text.secondary">{r.requestedBySN}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">{r.fromCommandCode}</Typography>
                  <Typography variant="caption" color="text.secondary">{r.fromZone}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">{r.toCommandCode}</Typography>
                  <Typography variant="caption" color="text.secondary">{r.toZone}</Typography>
                </TableCell>
                <TableCell sx={{maxWidth:180,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.reason}</TableCell>
                <TableCell><Chip label={r.status} size="small" color={r.status==='APPROVED'?'success':r.status==='REJECTED'?'error':'warning'}/></TableCell>
                <TableCell sx={{maxWidth:140,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.adminNote}</TableCell>
                <TableCell><Typography variant="caption">{new Date(r.requestedAt).toLocaleDateString('en-GB')}</Typography></TableCell>
              </TableRow>
            ))}
            {requests.length === 0 && <TableRow><TableCell colSpan={10} align="center" sx={{py:4,color:'text.secondary'}}>No transfer requests found</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Paper>
    </ReportTemplate>
  );
};
export default TransferRequestsReport;